import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  Loader2,
  Mail,
  MailOpen
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import websocketService from '../services/websocket';

const PAGE_LIMIT = 20;
const NOTIFICATION_COUNT_EVENT = 'admin-notification-count-changed';

const announceUnreadCountChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(NOTIFICATION_COUNT_EVENT));
};

const getStatusClassName = (status) => ({
  delivered: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  sent: 'bg-cyan-50 text-cyan-700 ring-cyan-200',
  failed: 'bg-red-50 text-red-700 ring-red-200',
  skipped: 'bg-slate-100 text-slate-700 ring-slate-200',
  pending: 'bg-amber-50 text-amber-700 ring-amber-200'
}[status] || 'bg-slate-100 text-slate-700 ring-slate-200');

export default function NotificationCenter() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_LIMIT, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState('');

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-PH', { timeZone: 'Asia/Manila' });
  };

  const loadNotifications = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    setError('');
    try {
      const response = await notificationAPI.list({ page, limit: PAGE_LIMIT, unreadOnly });
      const payload = response.data || {};
      setNotifications(Array.isArray(payload.data) ? payload.data : []);
      setPagination(payload.pagination || { total: 0, page, limit: PAGE_LIMIT, totalPages: 1 });
    } catch (loadError) {
      setError(loadError.response?.data?.message || loadError.message || t('notificationCenter.errors.load'));
    } finally {
      if (!background) setLoading(false);
    }
  }, [page, t, unreadOnly]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => websocketService.on('device_alert_notification', () => {
    loadNotifications({ background: true });
  }), [loadNotifications]);

  const changeUnreadFilter = (checked) => {
    setPage(1);
    setUnreadOnly(checked);
  };

  const markRead = async (notification) => {
    if (notification.readAt || actionId !== null) return;
    setActionId(notification.id);
    setError('');
    try {
      const response = await notificationAPI.markRead(notification.id);
      const updated = response.data?.data;
      if (unreadOnly) {
        setNotifications((current) => current.filter((item) => item.id !== notification.id));
        setPagination((current) => ({ ...current, total: Math.max(Number(current.total || 0) - 1, 0) }));
      } else {
        setNotifications((current) => current.map((item) => (
          item.id === notification.id ? { ...item, readAt: updated?.readAt || new Date().toISOString() } : item
        )));
      }
      announceUnreadCountChanged();
    } catch (actionError) {
      setError(actionError.response?.data?.message || actionError.message || t('notificationCenter.errors.markRead'));
    } finally {
      setActionId(null);
    }
  };

  const markAllRead = async () => {
    if (markingAll || !notifications.some((item) => !item.readAt)) return;
    setMarkingAll(true);
    setError('');
    try {
      await notificationAPI.markAllRead();
      if (unreadOnly) {
        setNotifications([]);
        setPagination((current) => ({ ...current, total: 0, totalPages: 1 }));
      } else {
        const readAt = new Date().toISOString();
        setNotifications((current) => current.map((item) => ({ ...item, readAt: item.readAt || readAt })));
      }
      announceUnreadCountChanged();
    } catch (actionError) {
      setError(actionError.response?.data?.message || actionError.message || t('notificationCenter.errors.markAllRead'));
    } finally {
      setMarkingAll(false);
    }
  };

  const hasUnread = notifications.some((notification) => !notification.readAt);

  return (
    <div className="min-h-screen bg-[var(--admin-page-bg)] p-4 text-[var(--admin-text-primary)] sm:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <h1 className="flex items-center gap-3 text-2xl font-black leading-9 sm:text-3xl">
              <span className="admin-muted-panel flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--admin-primary)]">
                <Bell size={24} />
              </span>
              <span>{t('notificationCenter.title')}</span>
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--admin-text-muted)]">
              {t('notificationCenter.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={markAllRead}
            disabled={!hasUnread || markingAll}
            className="admin-primary-button inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-black disabled:cursor-not-allowed disabled:opacity-50"
          >
            {markingAll ? <Loader2 size={17} className="animate-spin" /> : <CheckCheck size={17} />}
            {t('notificationCenter.markAllRead')}
          </button>
        </header>

        <section className="admin-card rounded-xl p-4 sm:p-5" aria-label={t('notificationCenter.filters')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="inline-flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)] px-4 text-sm font-bold">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(event) => changeUnreadFilter(event.target.checked)}
                className="h-4 w-4 accent-[var(--admin-primary)]"
              />
              {t('notificationCenter.unreadOnly')}
            </label>
            <div className="text-sm font-bold text-[var(--admin-text-muted)]">
              {t('notificationCenter.total', { count: Number(pagination.total || 0) })}
            </div>
          </div>
        </section>

        {error && (
          <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}

        {loading ? (
          <div role="status" className="admin-card flex min-h-48 items-center justify-center gap-3 rounded-xl text-sm font-bold text-[var(--admin-text-muted)]">
            <Loader2 size={20} className="animate-spin" />
            {t('notificationCenter.loading')}
          </div>
        ) : notifications.length === 0 ? (
          <div className="admin-card flex min-h-48 flex-col items-center justify-center rounded-xl p-6 text-center">
            <MailOpen size={34} className="text-[var(--admin-text-muted)]" />
            <p className="mt-3 text-sm font-black text-[var(--admin-text-secondary)]">{t('notificationCenter.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3" aria-live="polite">
            {notifications.map((notification) => (
              <article
                key={notification.id}
                className={`admin-card rounded-xl border-l-4 p-4 sm:p-5 ${
                  notification.readAt ? 'border-l-slate-300' : 'border-l-[var(--admin-primary)]'
                }`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {notification.readAt ? (
                        <MailOpen size={18} className="text-[var(--admin-text-muted)]" />
                      ) : (
                        <Mail size={18} className="text-[var(--admin-primary)]" />
                      )}
                      <h2 className="break-words text-base font-black text-[var(--admin-text-primary)]">
                        {notification.title || t('notificationCenter.untitled')}
                      </h2>
                      {!notification.readAt && (
                        <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-cyan-700 ring-1 ring-cyan-200">
                          {t('notificationCenter.unread')}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 break-words text-sm leading-6 text-[var(--admin-text-secondary)]">
                      {notification.body || '-'}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-bold text-[var(--admin-text-muted)]">
                      <span className="inline-flex items-center gap-1.5"><Clock3 size={14} />{formatDateTime(notification.createdAt)}</span>
                      <span>{t(`notificationCenter.recipientGroups.${notification.recipientGroup}`, { defaultValue: notification.recipientGroup || '-' })}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {!notification.readAt && (
                      <button
                        type="button"
                        onClick={() => markRead(notification)}
                        disabled={actionId !== null}
                        className="admin-secondary-button inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-black disabled:opacity-50"
                      >
                        {actionId === notification.id ? <Loader2 size={15} className="animate-spin" /> : <MailOpen size={15} />}
                        {t('notificationCenter.markRead')}
                      </button>
                    )}
                    {notification.alertId && (
                      <Link
                        to={`/device-alerts?alertId=${encodeURIComponent(notification.alertId)}`}
                        className="admin-primary-button inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-black"
                      >
                        <ExternalLink size={15} />
                        {t('notificationCenter.viewAlert')}
                      </Link>
                    )}
                  </div>
                </div>

                <div className="mt-4 border-t border-[var(--admin-border-subtle)] pt-3">
                  <p className="text-xs font-black uppercase tracking-wide text-[var(--admin-text-muted)]">
                    {t('notificationCenter.deliveries.title')}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(notification.deliveries || []).length ? notification.deliveries.map((delivery) => (
                      <span
                        key={delivery.id || `${delivery.channel}-${delivery.reminderSequence}`}
                        data-testid={delivery.id ? `notification-delivery-${delivery.id}` : undefined}
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-black ring-1 ${getStatusClassName(delivery.status)}`}
                      >
                        {t(`notificationCenter.deliveries.${delivery.channel}`, { defaultValue: delivery.channel })}
                        <span aria-hidden="true">·</span>
                        {t(`notificationCenter.status.${delivery.status}`, { defaultValue: delivery.status })}
                      </span>
                    )) : (
                      <span className="text-xs text-[var(--admin-text-muted)]">{t('notificationCenter.deliveries.empty')}</span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {Number(pagination.totalPages || 1) > 1 && (
          <nav className="admin-card flex items-center justify-between rounded-xl p-3" aria-label={t('notificationCenter.pagination')}>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={page <= 1 || loading}
              className="admin-secondary-button inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-xs font-black disabled:opacity-50"
            >
              <ChevronLeft size={16} />{t('notificationCenter.previous')}
            </button>
            <span className="text-xs font-black text-[var(--admin-text-muted)]">
              {t('notificationCenter.page', { page, totalPages: pagination.totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(current + 1, pagination.totalPages))}
              disabled={page >= pagination.totalPages || loading}
              className="admin-secondary-button inline-flex min-h-10 items-center gap-2 rounded-lg px-3 text-xs font-black disabled:opacity-50"
            >
              {t('notificationCenter.next')}<ChevronRight size={16} />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
}
